import { useState } from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { supabase } from '@shared/lib/supabase';
import { DARK_DEFAULTS, LIGHT_DEFAULTS } from '@checkout/useCheckoutTheme';
import type { MerchantBranding, FloatingAsset } from '@shared/types';
import ColorPickerField from './ColorPickerField';
import FontSelector from './FontSelector';
import ImageCropper from './ImageCropper';
import SaveBar from './SaveBar';
import CheckoutPreview from './CheckoutPreview';

interface CheckoutCustomizeTabProps {
  userId: string;
  branding: MerchantBranding;
  setBranding: (fn: (prev: MerchantBranding) => MerchantBranding) => void;
  merchantName: string;
  saving: boolean;
  saveSuccess: boolean;
  saveError: string;
  onSave: () => void;
}

const COLOR_FIELDS = [
  { key: 'primary_color', label: 'Primary' },
  { key: 'accent_color', label: 'Accent / Button' },
  { key: 'text_color', label: 'Text' },
  { key: 'background_color', label: 'Background' },
  { key: 'card_background_color', label: 'Card Background' },
  { key: 'border_color', label: 'Border' },
  { key: 'error_color', label: 'Error' },
  { key: 'success_color', label: 'Success' },
] as const;

const inputClass = 'w-full rounded px-3 py-2 font-sans text-sm focus:outline-none transition-colors';
const labelClass = 'block font-mono text-[10px] uppercase tracking-widest mb-1.5';

const inputStyle: React.CSSProperties = {
  border: '1px solid var(--dash-border)',
  background: 'var(--dash-input)',
  color: 'var(--dash-text-secondary)',
};

const labelStyle: React.CSSProperties = {
  color: 'var(--dash-text-muted)',
};

const selectStyle: React.CSSProperties = {
  ...inputStyle,
  color: 'var(--dash-select-text)',
};

function Section({ title, children, defaultOpen = true }: { title: string; children: React.ReactNode; defaultOpen?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="rounded" style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-card)' }}>
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-3 text-left"
      >
        <h2 className="font-mono text-[10px] uppercase tracking-widest" style={{ color: 'var(--dash-text-muted)' }}>{title}</h2>
        {open
          ? <ChevronDown size={14} style={{ color: 'var(--dash-text-faint)' }} />
          : <ChevronRight size={14} style={{ color: 'var(--dash-text-faint)' }} />
        }
      </button>
      {open && <div className="px-5 pb-5 space-y-4">{children}</div>}
    </div>
  );
}

export default function CheckoutCustomizeTab({
  userId,
  branding,
  setBranding,
  merchantName,
  saving,
  saveSuccess,
  saveError,
  onSave,
}: CheckoutCustomizeTabProps) {
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [uploadError, setUploadError] = useState('');
  const [cropFile, setCropFile] = useState<File | null>(null);
  const [uploadingBg, setUploadingBg] = useState(false);
  const [bgUploadError, setBgUploadError] = useState('');
  const [uploadingAsset, setUploadingAsset] = useState(false);
  const [assetUploadError, setAssetUploadError] = useState('');
  const [uploadLogoHover, setUploadLogoHover] = useState(false);
  const [removeLogoHover, setRemoveLogoHover] = useState(false);
  const [uploadBgHover, setUploadBgHover] = useState(false);
  const [removeBgHover, setRemoveBgHover] = useState(false);
  const [uploadAssetHover, setUploadAssetHover] = useState(false);
  const [removeAssetHoverIdx, setRemoveAssetHoverIdx] = useState<number | null>(null);

  const updateField = <K extends keyof MerchantBranding>(field: K, value: MerchantBranding[K]) => {
    setBranding((prev) => ({ ...prev, [field]: value }));
  };

  const markColorCustomized = (field: string) => {
    setBranding((prev) => {
      const existing = prev.customized_colors || [];
      if (existing.includes(field)) return prev;
      return { ...prev, customized_colors: [...existing, field] };
    });
  };

  const handleThemeSwitch = (theme: 'dark' | 'light') => {
    setBranding((prev) => {
      const customized = prev.customized_colors || [];
      const defaults = theme === 'light' ? LIGHT_DEFAULTS : DARK_DEFAULTS;
      const updated: Record<string, unknown> = { ...prev, checkout_theme: theme };

      for (const { key } of COLOR_FIELDS) {
        if (!customized.includes(key)) {
          updated[key] = defaults[key as keyof typeof defaults] || undefined;
        }
      }

      return updated as MerchantBranding;
    });
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setUploadError('Please select an image file');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setUploadError('Image must be under 5MB');
      return;
    }
    setUploadError('');
    setCropFile(file);
    e.target.value = '';
  };

  const handleCrop = async (blob: Blob) => {
    setCropFile(null);
    setUploadingLogo(true);
    setUploadError('');

    const filePath = `${userId}/checkout-logo.png`;
    const { error: uploadErr } = await supabase.storage
      .from('avatars')
      .upload(filePath, blob, { upsert: true, contentType: 'image/png' });

    if (uploadErr) {
      setUploadError(uploadErr.message);
      setUploadingLogo(false);
      return;
    }

    const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(filePath);
    if (urlData?.publicUrl) {
      updateField('logo_url', `${urlData.publicUrl}?t=${Date.now()}`);
    }
    setUploadingLogo(false);
  };

  const handleRemoveLogo = async () => {
    if (!branding.logo_url) return;
    setUploadingLogo(true);
    const filePath = `${userId}/checkout-logo.png`;
    await supabase.storage.from('avatars').remove([filePath]);
    updateField('logo_url', undefined);
    setUploadingLogo(false);
  };

  const handleBgImageUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.startsWith('image/')) {
      setBgUploadError('Please select an image file');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      setBgUploadError('Image must be under 2MB');
      return;
    }
    setBgUploadError('');
    setUploadingBg(true);

    const ext = file.name.split('.').pop() || 'jpg';
    const filePath = `${userId}/checkout-bg.${ext}`;
    const { error: uploadErr } = await supabase.storage
      .from('avatars')
      .upload(filePath, file, { upsert: true, contentType: file.type });

    if (uploadErr) {
      setBgUploadError(uploadErr.message);
      setUploadingBg(false);
      return;
    }

    const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(filePath);
    if (urlData?.publicUrl) {
      updateField('background_image_url', `${urlData.publicUrl}?t=${Date.now()}`);
    }
    setUploadingBg(false);
  };

  const handleRemoveBgImage = async () => {
    if (!branding.background_image_url) return;
    setUploadingBg(true);
    await supabase.storage.from('avatars').remove([`${userId}/checkout-bg.jpg`, `${userId}/checkout-bg.png`, `${userId}/checkout-bg.webp`]);
    updateField('background_image_url', undefined);
    setUploadingBg(false);
  };

  const handleAssetUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file) return;
    if (!file.type.match(/^image\/(png|webp)$/)) {
      setAssetUploadError('Only PNG or WebP allowed');
      return;
    }
    if (file.size > 200 * 1024) {
      setAssetUploadError('Asset must be under 200KB');
      return;
    }
    setAssetUploadError('');
    setUploadingAsset(true);

    const idx = (branding.floating_assets || []).length;
    const ext = file.name.split('.').pop() || 'png';
    const filePath = `${userId}/float-${idx}.${ext}`;
    const { error: uploadErr } = await supabase.storage
      .from('avatars')
      .upload(filePath, file, { upsert: true, contentType: file.type });

    if (uploadErr) {
      setAssetUploadError(uploadErr.message);
      setUploadingAsset(false);
      return;
    }

    const { data: urlData } = supabase.storage.from('avatars').getPublicUrl(filePath);
    if (urlData?.publicUrl) {
      const newAsset: FloatingAsset = {
        url: `${urlData.publicUrl}?t=${Date.now()}`,
        size: 80,
        count: 3,
        animation: 'float',
        speed: 'medium',
        opacity: 60,
      };
      setBranding((prev) => ({
        ...prev,
        floating_assets: [...(prev.floating_assets || []), newAsset],
      }));
    }
    setUploadingAsset(false);
  };

  const updateAsset = (index: number, field: keyof FloatingAsset, value: FloatingAsset[keyof FloatingAsset]) => {
    setBranding((prev) => {
      const assets = [...(prev.floating_assets || [])];
      assets[index] = { ...assets[index], [field]: value };
      return { ...prev, floating_assets: assets };
    });
  };

  const removeAsset = async (index: number) => {
    const asset = branding.floating_assets?.[index];
    if (asset) {
      const urlParts = asset.url.split('?')[0].split('/');
      const fileName = urlParts[urlParts.length - 1];
      await supabase.storage.from('avatars').remove([`${userId}/${fileName}`]);
    }
    setBranding((prev) => ({
      ...prev,
      floating_assets: (prev.floating_assets || []).filter((_, i) => i !== index),
    }));
  };

  const currentTheme = branding.checkout_theme || 'dark';

  return (
    <>
      {cropFile && (
        <ImageCropper
          imageFile={cropFile}
          onCrop={handleCrop}
          onCancel={() => setCropFile(null)}
        />
      )}

      <div className="flex gap-6">
        <div className="flex-1 min-w-0 space-y-4">
          <Section title="Brand Identity">
            <div>
              <label className={labelClass} style={labelStyle}>Checkout Logo</label>
              <div className="flex items-center gap-3">
                <div
                  className="relative w-16 h-16 rounded flex items-center justify-center overflow-hidden flex-shrink-0"
                  style={{ border: '1px solid var(--dash-border)', background: 'var(--dash-avatar-bg)' }}
                >
                  {branding.logo_url ? (
                    <img src={branding.logo_url} alt="Logo" className="w-full h-full object-contain" />
                  ) : (
                    <span className="text-xs font-mono" style={{ color: 'var(--dash-text-faint)' }}>LOGO</span>
                  )}
                  {uploadingLogo && (
                    <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                      <span className="inline-block animate-spin text-white text-sm">&#9676;</span>
                    </div>
                  )}
                </div>
                <div>
                  <div className="flex items-center gap-2">
                    <label
                      className="inline-flex items-center gap-2 cursor-pointer font-mono text-xs transition-colors rounded px-3 py-1.5"
                      style={{
                        color: uploadLogoHover ? 'var(--dash-text-secondary)' : 'var(--dash-text-muted)',
                        border: '1px solid var(--dash-border)',
                      }}
                      onMouseEnter={() => setUploadLogoHover(true)}
                      onMouseLeave={() => setUploadLogoHover(false)}
                    >
                      <span className="text-[11px]">&uarr;</span>
                      Upload Logo
                      <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleFileSelect} className="hidden" />
                    </label>
                    {branding.logo_url && (
                      <button
                        onClick={handleRemoveLogo}
                        className="font-mono text-xs transition-colors rounded px-3 py-1.5"
                        style={{
                          color: removeLogoHover ? 'var(--color-red-400, #f87171)' : 'var(--dash-text-faint)',
                          border: '1px solid var(--dash-border)',
                        }}
                        onMouseEnter={() => setRemoveLogoHover(true)}
                        onMouseLeave={() => setRemoveLogoHover(false)}
                      >
                        Remove
                      </button>
                    )}
                  </div>
                  <p className="font-sans text-[10px] mt-1" style={{ color: 'var(--dash-text-faint)' }}>Up to 400px wide. PNG, SVG, or WebP. Under 200KB.</p>
                  {uploadError && <p className="font-sans text-[10px] text-red-400 mt-1">{uploadError}</p>}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className={labelClass} style={labelStyle}>Logo Position</label>
                <select
                  value={branding.logo_position || 'left'}
                  onChange={(e) => updateField('logo_position', e.target.value as 'left' | 'center')}
                  className={inputClass}
                  style={selectStyle}
                >
                  <option value="left">Left</option>
                  <option value="center">Center</option>
                </select>
              </div>
              <div>
                <label className={labelClass} style={labelStyle}>Logo Size</label>
                <select
                  value={branding.logo_size || 'medium'}
                  onChange={(e) => updateField('logo_size', e.target.value as MerchantBranding['logo_size'])}
                  className={inputClass}
                  style={selectStyle}
                >
                  <option value="small">Small (32px)</option>
                  <option value="medium">Medium (56px)</option>
                  <option value="large">Large (80px)</option>
                  <option value="xlarge">Extra Large (112px)</option>
                  <option value="custom">Custom</option>
                </select>
              </div>
            </div>
            {branding.logo_size === 'custom' && (
              <div>
                <label className={labelClass} style={labelStyle}>Custom Logo Height (px)</label>
                <input
                  type="number"
                  min={20}
                  max={200}
                  value={branding.logo_custom_height || 56}
                  onChange={(e) => updateField('logo_custom_height', Math.min(200, Math.max(20, parseInt(e.target.value) || 56)))}
                  className={inputClass}
                  style={inputStyle}
                />
                <p className="font-sans text-[10px] mt-1" style={{ color: 'var(--dash-text-faint)' }}>Between 20px and 200px</p>
              </div>
            )}
            <div>
              <label className={labelClass} style={labelStyle}>Business Name Override</label>
              <input
                type="text"
                value={branding.business_name || ''}
                onChange={(e) => updateField('business_name', e.target.value || undefined)}
                placeholder="Defaults to your profile business name"
                className={inputClass}
                style={inputStyle}
              />
            </div>
          </Section>

          <Section title="Colors">
            <div>
              <label className={labelClass} style={labelStyle}>Theme</label>
              <div className="flex rounded overflow-hidden" style={{ border: '1px solid var(--dash-border)' }}>
                <button
                  type="button"
                  onClick={() => handleThemeSwitch('dark')}
                  className="flex-1 py-2 text-xs font-mono uppercase tracking-wider transition-colors"
                  style={
                    currentTheme === 'dark'
                      ? { background: 'var(--dash-hover-bg)', color: 'var(--dash-text-primary)' }
                      : { background: 'var(--dash-input)', color: 'var(--dash-text-muted)' }
                  }
                >
                  Dark
                </button>
                <button
                  type="button"
                  onClick={() => handleThemeSwitch('light')}
                  className="flex-1 py-2 text-xs font-mono uppercase tracking-wider transition-colors"
                  style={
                    currentTheme === 'light'
                      ? { background: 'var(--dash-hover-bg)', color: 'var(--dash-text-primary)' }
                      : { background: 'var(--dash-input)', color: 'var(--dash-text-muted)' }
                  }
                >
                  Light
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              {COLOR_FIELDS.map(({ key, label }) => {
                const defaults = currentTheme === 'light' ? LIGHT_DEFAULTS : DARK_DEFAULTS;
                return (
                  <ColorPickerField
                    key={key}
                    label={label}
                    value={branding[key as keyof MerchantBranding] as string || defaults[key as keyof typeof defaults] || ''}
                    onChange={(hex) => updateField(key as keyof MerchantBranding, hex as never)}
                    onExplicitSet={() => markColorCustomized(key)}
                    placeholder={defaults[key as keyof typeof defaults]}
                  />
                );
              })}
            </div>
          </Section>

          <Section title="Background">
            <div>
              <label className={labelClass} style={labelStyle}>Background Type</label>
              <select
                value={branding.background_type || 'color'}
                onChange={(e) => updateField('background_type', e.target.value as MerchantBranding['background_type'])}
                className={inputClass}
                style={selectStyle}
              >
                <option value="color">Solid Color</option>
                <option value="gradient">Gradient</option>
                <option value="image">Image</option>
              </select>
            </div>

            {(!branding.background_type || branding.background_type === 'color') && (
              <ColorPickerField
                label="Background Color"
                value={branding.background_color || (currentTheme === 'light' ? LIGHT_DEFAULTS.background_color : DARK_DEFAULTS.background_color)}
                onChange={(hex) => updateField('background_color', hex)}
                onExplicitSet={() => markColorCustomized('background_color')}
                placeholder={currentTheme === 'light' ? LIGHT_DEFAULTS.background_color : DARK_DEFAULTS.background_color}
              />
            )}

            {branding.background_type === 'gradient' && (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <ColorPickerField
                    label="Start Color"
                    value={branding.background_gradient_start || (currentTheme === 'light' ? '#e2e8f0' : '#1a1a2e')}
                    onChange={(hex) => updateField('background_gradient_start', hex)}
                  />
                  <ColorPickerField
                    label="End Color"
                    value={branding.background_gradient_end || (currentTheme === 'light' ? '#f8fafc' : '#16213e')}
                    onChange={(hex) => updateField('background_gradient_end', hex)}
                  />
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Direction</label>
                  <select
                    value={branding.background_gradient_direction || 'to bottom'}
                    onChange={(e) => updateField('background_gradient_direction', e.target.value as MerchantBranding['background_gradient_direction'])}
                    className={inputClass}
                    style={selectStyle}
                  >
                    <option value="to bottom">Top to Bottom</option>
                    <option value="to top">Bottom to Top</option>
                    <option value="to right">Left to Right</option>
                    <option value="to left">Right to Left</option>
                    <option value="to bottom right">Diagonal (top-left to bottom-right)</option>
                    <option value="to top right">Diagonal (bottom-left to top-right)</option>
                  </select>
                </div>
              </>
            )}

            {branding.background_type === 'image' && (
              <>
                <div>
                  <label className={labelClass} style={labelStyle}>Background Image</label>
                  <p className="font-sans text-[10px] mb-2" style={{ color: 'var(--dash-text-faint)' }}>
                    Recommended: 1920x1080px minimum, JPEG or WebP, under 2MB for fast loading.
                  </p>
                  <div className="flex items-center gap-3">
                    {branding.background_image_url && (
                      <div className="relative w-20 h-14 rounded overflow-hidden flex-shrink-0" style={{ border: '1px solid var(--dash-border)' }}>
                        <img src={branding.background_image_url} alt="Background" className="w-full h-full object-cover" />
                        {uploadingBg && (
                          <div className="absolute inset-0 bg-black/60 flex items-center justify-center">
                            <span className="inline-block animate-spin text-white text-sm">&#9676;</span>
                          </div>
                        )}
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <label
                        className="inline-flex items-center gap-2 cursor-pointer font-mono text-xs transition-colors rounded px-3 py-1.5"
                        style={{
                          color: uploadBgHover ? 'var(--dash-text-secondary)' : 'var(--dash-text-muted)',
                          border: '1px solid var(--dash-border)',
                        }}
                        onMouseEnter={() => setUploadBgHover(true)}
                        onMouseLeave={() => setUploadBgHover(false)}
                      >
                        <span className="text-[11px]">&uarr;</span>
                        {branding.background_image_url ? 'Replace' : 'Upload'}
                        <input type="file" accept="image/jpeg,image/png,image/webp" onChange={handleBgImageUpload} className="hidden" />
                      </label>
                      {branding.background_image_url && (
                        <button
                          onClick={handleRemoveBgImage}
                          className="font-mono text-xs transition-colors rounded px-3 py-1.5"
                          style={{
                            color: removeBgHover ? 'var(--color-red-400, #f87171)' : 'var(--dash-text-faint)',
                            border: '1px solid var(--dash-border)',
                          }}
                          onMouseEnter={() => setRemoveBgHover(true)}
                          onMouseLeave={() => setRemoveBgHover(false)}
                        >
                          Remove
                        </button>
                      )}
                    </div>
                  </div>
                  {bgUploadError && <p className="font-sans text-[10px] text-red-400 mt-1">{bgUploadError}</p>}
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Image Overlay ({branding.background_image_overlay ?? 40}%)</label>
                  <input
                    type="range"
                    min={0}
                    max={90}
                    value={branding.background_image_overlay ?? 40}
                    onChange={(e) => updateField('background_image_overlay', parseInt(e.target.value))}
                    className="w-full accent-zinc-500"
                  />
                  <p className="font-sans text-[10px] mt-1" style={{ color: 'var(--dash-text-faint)' }}>
                    Darkens the image so text stays readable. 0% = no overlay, 90% = very dark.
                  </p>
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Image Blur ({branding.background_image_blur || 0}px)</label>
                  <input
                    type="range"
                    min={0}
                    max={20}
                    value={branding.background_image_blur || 0}
                    onChange={(e) => updateField('background_image_blur', parseInt(e.target.value))}
                    className="w-full accent-zinc-500"
                  />
                </div>
                <div>
                  <label className={labelClass} style={labelStyle}>Image Size</label>
                  <select
                    value={branding.background_image_size || 'cover'}
                    onChange={(e) => updateField('background_image_size', e.target.value as MerchantBranding['background_image_size'])}
                    className={inputClass}
                    style={selectStyle}
                  >
                    <option value="cover">Cover (fill screen)</option>
                    <option value="contain">Contain (fit inside)</option>
                    <option value="repeat">Repeat (tile)</option>
                  </select>
                </div>
              </>
            )}
          </Section>

          <Section title="Typography">
            <FontSelector
              label="Heading Font"
              value={branding.heading_font || 'IBM Plex Sans'}
              onChange={(f) => updateField('heading_font', f)}
            />
            <FontSelector
              label="Body Font"
              value={branding.body_font || 'IBM Plex Sans'}
              onChange={(f) => updateField('body_font', f)}
            />
          </Section>

          <Section title="Content">
            <div>
              <label className={labelClass} style={labelStyle}>Checkout Title</label>
              <input
                type="text"
                value={branding.checkout_title || ''}
                onChange={(e) => updateField('checkout_title', e.target.value || undefined)}
                placeholder="Complete Your Purchase"
                className={inputClass}
                style={inputStyle}
              />
            </div>
            <div>
              <label className={labelClass} style={labelStyle}>Checkout Description</label>
              <textarea
                value={branding.checkout_description || ''}
                onChange={(e) => updateField('checkout_description', e.target.value || undefined)}
                placeholder="Secure payment for your order"
                rows={2}
                className={inputClass}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
            </div>
            <div>
              <label className={labelClass} style={labelStyle}>Footer Text</label>
              <input
                type="text"
                value={branding.checkout_footer_text || ''}
                onChange={(e) => updateField('checkout_footer_text', e.target.value || undefined)}
                placeholder="Questions? Contact support@yourbusiness.com"
                className={inputClass}
                style={inputStyle}
              />
            </div>
            <div>
              <label className={labelClass} style={labelStyle}>Success Message</label>
              <input
                type="text"
                value={branding.checkout_success_message || ''}
                onChange={(e) => updateField('checkout_success_message', e.target.value || undefined)}
                placeholder="Thank you! Your payment was successful."
                className={inputClass}
                style={inputStyle}
              />
            </div>
          </Section>

          <Section title="Layout">
            <div>
              <label className={labelClass} style={labelStyle}>Corner Radius</label>
              <select
                value={branding.corner_radius || 'medium'}
                onChange={(e) => updateField('corner_radius', e.target.value as MerchantBranding['corner_radius'])}
                className={inputClass}
                style={selectStyle}
              >
                <option value="none">None</option>
                <option value="small">Small</option>
                <option value="medium">Medium</option>
                <option value="large">Large</option>
              </select>
            </div>
            <div className="space-y-3">
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={branding.show_order_summary !== false}
                  onChange={(e) => updateField('show_order_summary', e.target.checked)}
                  className="rounded border-zinc-700 bg-zinc-900 text-blue-600 focus:ring-0 focus:ring-offset-0"
                />
                <span className="font-sans text-sm" style={{ color: 'var(--dash-text-tertiary)' }}>Show order summary</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={branding.show_secure_badge !== false}
                  onChange={(e) => updateField('show_secure_badge', e.target.checked)}
                  className="rounded border-zinc-700 bg-zinc-900 text-blue-600 focus:ring-0 focus:ring-offset-0"
                />
                <span className="font-sans text-sm" style={{ color: 'var(--dash-text-tertiary)' }}>Show security badge</span>
              </label>
              <label className="flex items-center gap-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={branding.show_powered_by !== false}
                  onChange={(e) => updateField('show_powered_by', e.target.checked)}
                  className="rounded border-zinc-700 bg-zinc-900 text-blue-600 focus:ring-0 focus:ring-offset-0"
                />
                <span className="font-sans text-sm" style={{ color: 'var(--dash-text-tertiary)' }}>{`Show "Powered by BonesPay"`}</span>
              </label>
            </div>
          </Section>

          <Section title="Decorative Assets" defaultOpen={false}>
            <div className="flex items-center justify-between">
              <div>
                <label className={labelClass} style={labelStyle}>Floating Decorative Assets</label>
                <p className="font-sans text-[10px] -mt-1" style={{ color: 'var(--dash-text-faint)' }}>
                  Animated PNGs that float across the checkout background
                </p>
              </div>
              <input
                type="checkbox"
                checked={branding.floating_assets_enabled || false}
                onChange={(e) => updateField('floating_assets_enabled', e.target.checked)}
                className="rounded border-zinc-700 bg-zinc-900 text-blue-600 focus:ring-0 focus:ring-offset-0"
              />
            </div>

            {branding.floating_assets_enabled && (
              <div className="space-y-3">
                <p className="font-sans text-[10px]" style={{ color: 'var(--dash-text-faint)' }}>
                  Upload transparent PNG images (100-200px, under 200KB each). Max 5 assets.
                </p>

                {(branding.floating_assets || []).map((asset, i) => (
                  <div key={i} className="rounded p-3" style={{ border: '1px solid var(--dash-border-light)', background: 'var(--dash-input)' }}>
                    <div className="flex items-start gap-3">
                      <div
                        className="w-12 h-12 rounded flex items-center justify-center overflow-hidden flex-shrink-0"
                        style={{ background: 'var(--dash-avatar-bg)' }}
                      >
                        <img src={asset.url} alt="" className="w-full h-full object-contain" />
                      </div>
                      <div className="flex-1 min-w-0 space-y-2">
                        <div className="flex gap-2">
                          <select
                            value={asset.animation}
                            onChange={(e) => updateAsset(i, 'animation', e.target.value as FloatingAsset['animation'])}
                            className={`${inputClass} !w-auto`}
                            style={selectStyle}
                          >
                            <option value="bounce">Bounce</option>
                            <option value="float">Float</option>
                            <option value="spin">Spin</option>
                            <option value="drift">Drift</option>
                          </select>
                          <select
                            value={asset.speed}
                            onChange={(e) => updateAsset(i, 'speed', e.target.value as FloatingAsset['speed'])}
                            className={`${inputClass} !w-auto`}
                            style={selectStyle}
                          >
                            <option value="slow">Slow</option>
                            <option value="medium">Medium</option>
                            <option value="fast">Fast</option>
                          </select>
                        </div>
                        <div className="flex gap-3 items-center flex-wrap">
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-[9px]" style={{ color: 'var(--dash-text-faint)' }}>Size</span>
                            <input
                              type="number"
                              min={40}
                              max={200}
                              value={asset.size}
                              onChange={(e) => updateAsset(i, 'size', Math.min(200, Math.max(40, parseInt(e.target.value) || 80)))}
                              className={`${inputClass} !w-16`}
                              style={inputStyle}
                            />
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-[9px]" style={{ color: 'var(--dash-text-faint)' }}>Count</span>
                            <input
                              type="number"
                              min={1}
                              max={20}
                              value={asset.count}
                              onChange={(e) => updateAsset(i, 'count', Math.min(20, Math.max(1, parseInt(e.target.value) || 3)))}
                              className={`${inputClass} !w-16`}
                              style={inputStyle}
                            />
                          </div>
                          <div className="flex items-center gap-1.5">
                            <span className="font-mono text-[9px]" style={{ color: 'var(--dash-text-faint)' }}>Opacity</span>
                            <input
                              type="range"
                              min={10}
                              max={100}
                              value={asset.opacity}
                              onChange={(e) => updateAsset(i, 'opacity', parseInt(e.target.value))}
                              className="w-20 accent-zinc-500"
                            />
                            <span className="font-mono text-[9px]" style={{ color: 'var(--dash-text-faint)' }}>{asset.opacity}%</span>
                          </div>
                        </div>
                      </div>
                      <button
                        onClick={() => removeAsset(i)}
                        className="transition-colors text-lg leading-none flex-shrink-0 mt-1"
                        style={{ color: removeAssetHoverIdx === i ? 'var(--color-red-400, #f87171)' : 'var(--dash-text-faint)' }}
                        onMouseEnter={() => setRemoveAssetHoverIdx(i)}
                        onMouseLeave={() => setRemoveAssetHoverIdx(null)}
                      >
                        &times;
                      </button>
                    </div>
                  </div>
                ))}

                {(branding.floating_assets || []).length < 5 && (
                  <label
                    className="inline-flex items-center gap-2 cursor-pointer font-mono text-xs transition-colors rounded px-3 py-1.5"
                    style={{
                      color: uploadAssetHover ? 'var(--dash-text-secondary)' : 'var(--dash-text-muted)',
                      border: '1px solid var(--dash-border)',
                    }}
                    onMouseEnter={() => setUploadAssetHover(true)}
                    onMouseLeave={() => setUploadAssetHover(false)}
                  >
                    {uploadingAsset ? (
                      <span className="inline-block animate-spin text-sm">&#9676;</span>
                    ) : (
                      <span className="text-[11px]">+</span>
                    )}
                    Add Asset
                    <input type="file" accept="image/png,image/webp" onChange={handleAssetUpload} className="hidden" disabled={uploadingAsset} />
                  </label>
                )}
                {assetUploadError && <p className="font-sans text-[10px] text-red-400">{assetUploadError}</p>}
              </div>
            )}
          </Section>

          <Section title="Advanced" defaultOpen={false}>
            <div>
              <label className={labelClass} style={labelStyle}>Custom CSS</label>
              <textarea
                value={branding.custom_css || ''}
                onChange={(e) => updateField('custom_css', e.target.value.slice(0, 5000) || undefined)}
                placeholder={`[data-checkout="root"] {\n  /* Your custom styles */\n}`}
                rows={6}
                maxLength={5000}
                className={`${inputClass} font-mono text-xs`}
                style={{ ...inputStyle, resize: 'vertical' }}
              />
              <p className="font-sans text-[10px] mt-1" style={{ color: 'var(--dash-text-faint)' }}>
                Custom CSS is injected into your checkout page. Maximum 5,000 characters.
              </p>
            </div>
            <div>
              <label className={labelClass} style={labelStyle}>Preview URL Format</label>
              <input
                type="text"
                readOnly
                value="https://checkout.bonespay.com/pay/{order_id}"
                className={`${inputClass} cursor-default`}
                style={{ ...inputStyle, color: 'var(--dash-text-muted)' }}
              />
            </div>
          </Section>

          <SaveBar saving={saving} saveSuccess={saveSuccess} saveError={saveError} onSave={onSave} />
        </div>

        <div className="hidden lg:block w-[400px] flex-shrink-0">
          <div className="sticky top-6">
            <h3 className="font-mono text-[10px] uppercase tracking-widest mb-3" style={{ color: 'var(--dash-text-muted)' }}>Live Preview</h3>
            <CheckoutPreview branding={branding} merchantName={merchantName} />
          </div>
        </div>
      </div>
    </>
  );
}
